import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X, Calendar, Package, Search, TrendingUp, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination, ITEMS_PER_PAGE } from '@/components/Pagination';
import { TableSkeleton } from '@/components/ui/Skeletons';

const PurchaseOrders = () => {
  const [orders, setOrders] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [receiveItems, setReceiveItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    po_number: '',
    expected_date: '',
    items: [],
  });

  const openReceiveDialog = (order) => {
    setSelectedOrder(order);
    // Initialize receive items with remaining quantities
    const itemsReceived = order.items_received || {};
    const items = order.items.map(item => {
      const received = itemsReceived[item.material_id] || 0;
      const remaining = item.quantity - received;
      return {
        material_id: item.material_id,
        material_name: item.material_name,
        ordered_quantity: item.quantity,
        already_received: received,
        remaining: remaining,
        receive_quantity: remaining // Default to receive all remaining
      };
    });
    setReceiveItems(items);
    setReceiveDialogOpen(true);
  };

  const handlePartialReceive = async () => {
    const itemsToReceive = receiveItems
      .filter(item => item.receive_quantity > 0)
      .map(item => ({
        material_id: item.material_id,
        received_quantity: item.receive_quantity
      }));

    if (itemsToReceive.length === 0) {
      toast.error('Please enter quantities to receive');
      return;
    }

    try {
      await axios.put(`${API}/purchase-orders/${selectedOrder.id}/receive`, itemsToReceive);
      toast.success('Materials received successfully');
      setReceiveDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive materials');
    }
  };

  const handleReceive = async (poId) => {
    if (!window.confirm('Mark this purchase order as fully received? This will update material stock.')) return;

    try {
      await axios.put(`${API}/purchase-orders/${poId}/receive`);
      toast.success('Purchase order received and stock updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive order');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ordersRes, materialsRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`),
        axios.get(`${API}/raw-materials`),
      ]);
      setOrders(ordersRes.data);
      setMaterials(materialsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      po_number: '',
      expected_date: '',
      items: [],
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { material_id: '', material_name: '', quantity: '' }],
    });
  };

  const removeItem = (index) => {
    const updated = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updated });
  };

  const updateItem = (index, field, value) => {
    const updated = [...formData.items];
    updated[index][field] = value;

    if (field === 'material_id') {
      const mat = materials.find((m) => m.id === value);
      if (mat) {
        updated[index].material_name = mat.name;
      }
    }

    setFormData({ ...formData, items: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...formData,
        expected_date: formData.expected_date ? new Date(formData.expected_date).toISOString() : null,
        items: formData.items.map((item) => ({
          ...item,
          quantity: parseFloat(item.quantity),
        })),
      };

      await axios.post(`${API}/purchase-orders`, data);
      toast.success('Purchase order created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    }
  };

  if (loading) {
    return (
      <div className="p-8 max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="h-10 w-64 bg-stone-200/60 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-stone-200/60 rounded animate-pulse"></div>
          </div>
          <div className="h-10 w-40 bg-stone-200/60 rounded animate-pulse"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-200/60 p-5 shadow-sm h-24 animate-pulse"></div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm p-6">
          <TableSkeleton rows={5} columns={6} />
        </div>
      </div>
    );
  }

  const totalOrders = orders.length;
  const receivedOrders = orders.filter(o => o.status === 'received').length;
  const pendingOrders = totalOrders - receivedOrders;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="purchase-orders-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-800 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Purchase Orders</h1>
          <p className="text-sm text-stone-500 mt-2 font-medium">Manage material procurement and supplier deliveries.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow shadow-indigo-200 transition-all font-medium" data-testid="add-po-button">
              <Plus size={16} className="mr-2" />
              New Purchase Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Purchase Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="po-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="po_number">PO Number</Label>
                  <Input
                    id="po_number"
                    value={formData.po_number}
                    onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                    required
                    data-testid="input-po-number"
                  />
                </div>
                <div>
                  <Label htmlFor="expected_date">Expected Date</Label>
                  <Input
                    id="expected_date"
                    type="date"
                    value={formData.expected_date}
                    onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
                    data-testid="input-expected-date"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Materials</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-po-item">
                    <Plus size={14} className="mr-1" /> Add Material
                  </Button>
                </div>
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="flex flex-col sm:flex-row gap-2 items-start sm:items-end bg-stone-50/50 p-3 rounded-lg border border-stone-100">
                      <div className="flex-1 w-full sm:w-auto relative mb-4 sm:mb-0">
                        <Label className="mb-1 block">Material</Label>
                        <Select
                          value={item.material_id}
                          onValueChange={(value) => updateItem(index, 'material_id', value)}
                        >
                          <SelectTrigger className="w-full bg-white" data-testid={`select-po-material-${index}`}>
                            <SelectValue placeholder="Select material" />
                          </SelectTrigger>
                          <SelectContent>
                            {materials.map((mat) => (
                              <SelectItem key={mat.id} value={mat.id}>
                                {mat.name} ({mat.code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-full sm:w-32">
                        <Label className="mb-1 block">Quantity</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          required
                          data-testid={`input-po-quantity-${index}`}
                        />
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)} className="w-full sm:w-auto justify-center mt-2 sm:mt-0" data-testid={`remove-po-item-${index}`}>
                        <X size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
                <Button type="submit" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow shadow-indigo-200 rounded-md px-6" data-testid="submit-po-button">
                  Create Order
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-stone-200/60 p-5 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
            <TrendingUp size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-stone-500">Total Purchase Orders</p>
            <p className="text-2xl font-bold text-stone-800">{totalOrders}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/60 p-5 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-amber-50 text-amber-600 rounded-lg">
            <Clock size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-stone-500">Pending Delivery</p>
            <p className="text-2xl font-bold text-stone-800">{pendingOrders}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-stone-200/60 p-5 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg">
            <CheckCircle2 size={24} />
          </div>
          <div>
            <p className="text-sm font-medium text-stone-500">Fully Received</p>
            <p className="text-2xl font-bold text-stone-800">{receivedOrders}</p>
          </div>
        </div>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400" size={18} />
          <Input
            placeholder="Search by PO number or status..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="pl-10 bg-white border-stone-200 focus-visible:ring-indigo-500 shadow-sm rounded-lg"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-stone-200/80 shadow-sm overflow-hidden mb-6">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full" data-testid="po-table">
            <thead className="bg-stone-50/80 border-b border-stone-100/80">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">PO Number</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Expected Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Items Overview</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {(() => {
                const filtered = orders.filter(o => {
                  if (!searchTerm) return true;
                  const q = searchTerm.toLowerCase();
                  return o.po_number.toLowerCase().includes(q) || o.status.toLowerCase().includes(q) || (o.pr_number || '').toLowerCase().includes(q);
                });
                const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
                return paginated.map((order) => {
                  const isReceived = order.status === 'received';
                  const isPartial = order.status === 'partial';
                  return (
                    <tr key={order.id} className="hover:bg-indigo-50/30 transition-colors group" data-testid={`po-row-${order.po_number}`}>
                      <td className="px-6 py-4 text-sm font-medium text-indigo-900">
                        {order.po_number}
                        {order.pr_number && (
                          <span className="ml-2 text-xs font-normal text-indigo-400 bg-indigo-50 px-2 py-0.5 rounded-md border border-indigo-100">from {order.pr_number}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-500">{new Date(order.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</td>
                      <td className="px-6 py-4 text-sm text-stone-500">
                        {order.expected_date ? new Date(order.expected_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {order.items.map((item, i) => {
                            const itemsReceived = order.items_received || {};
                            const received = itemsReceived[item.material_id] || 0;
                            return (
                              <div key={i} className="flex items-center text-xs">
                                <span className="text-stone-700 font-medium truncate max-w-[120px]" title={item.material_name}>{item.material_name || 'Material'}</span>
                                <span className="mx-2 text-stone-300">-</span>
                                <span className={`font-mono ${received < item.quantity ? 'text-amber-600' : 'text-emerald-600'}`}>
                                  {received} / {item.quantity}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${isReceived ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          isPartial ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isReceived ? 'bg-emerald-500' : isPartial ? 'bg-blue-500' : 'bg-amber-500'}`}></span>
                          {isPartial ? 'Partial' : order.status === 'received' ? 'Received' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {!isReceived && (
                          <div className="flex justify-end gap-2 transition-opacity">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openReceiveDialog(order)}
                              className="h-8 text-xs font-medium bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                              data-testid={`partial-receive-po-${order.po_number}`}
                            >
                              <Package size={14} className="mr-1" /> Receive
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReceive(order.id)}
                              className="h-8 text-xs font-medium bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                              data-testid={`receive-all-po-${order.po_number}`}
                            >
                              Receive All
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                });
              })()}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden flex flex-col divide-y divide-stone-100">
          {(() => {
            const filtered = orders.filter(o => {
              if (!searchTerm) return true;
              const q = searchTerm.toLowerCase();
              return o.po_number.toLowerCase().includes(q) || o.status.toLowerCase().includes(q) || (o.pr_number || '').toLowerCase().includes(q);
            });
            return filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((order) => {
              const isReceived = order.status === 'received';
              const isPartial = order.status === 'partial';
              return (
                <div key={`mobile-${order.id}`} className="p-4 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold text-indigo-900">{order.po_number}</h4>
                      {order.pr_number && (
                        <p className="text-xs text-indigo-400 mt-0.5">from {order.pr_number}</p>
                      )}
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium border ${isReceived ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      isPartial ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                      }`}>
                      {isPartial ? 'Partial' : isReceived ? 'Received' : 'Pending'}
                    </span>
                  </div>

                  <div className="flex justify-between text-xs text-stone-500 mb-3">
                    <span>Date: {new Date(order.date).toLocaleDateString()}</span>
                    <span>Expected: {order.expected_date ? new Date(order.expected_date).toLocaleDateString() : 'N/A'}</span>
                  </div>

                  <div className="bg-stone-50 rounded-md p-2 mb-3">
                    <p className="text-xs font-medium text-stone-700 mb-1 border-b border-stone-200 pb-1">Items</p>
                    <div className="space-y-1">
                      {order.items.map((item, i) => {
                        const itemsReceived = order.items_received || {};
                        const received = itemsReceived[item.material_id] || 0;
                        return (
                          <div key={i} className="flex justify-between items-center text-[10px]">
                            <span className="text-stone-700 truncate pr-2 max-w-[150px]">{item.material_name || 'Material'}</span>
                            <span className={`font-mono ${received < item.quantity ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {received} / {item.quantity}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {!isReceived && (
                    <div className="flex gap-2 justify-end pt-2">
                      <Button variant="outline" size="sm" onClick={() => openReceiveDialog(order)} className="flex-1 h-8 text-xs text-indigo-600">
                        <Package size={14} className="mr-1" /> Partial
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleReceive(order.id)} className="flex-1 h-8 text-xs text-emerald-600">
                        <CheckCircle2 size={14} className="mr-1" /> Receive All
                      </Button>
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      </div>
      <div className="flex justify-center mt-2">
        <Pagination currentPage={currentPage} totalItems={orders.filter(o => { if (!searchTerm) return true; const q = searchTerm.toLowerCase(); return o.po_number.toLowerCase().includes(q) || o.status.toLowerCase().includes(q) || (o.pr_number || '').toLowerCase().includes(q); }).length} onPageChange={setCurrentPage} />
      </div>

      {/* Partial Receive Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Receive Materials - {selectedOrder?.po_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[hsl(145,20%,40%)]">
              Enter the quantity received for each material. Leave as 0 if not received yet.
            </p>

            <div className="overflow-x-auto">
              {/* Desktop Table */}
              <table className="w-full text-sm hidden sm:table">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600">Material</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600">Ordered</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600">Received</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600">Remaining</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-stone-600">Receive Now</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {receiveItems.map((item, idx) => (
                    <tr key={item.material_id} className={item.remaining === 0 ? 'bg-green-50/30' : ''}>
                      <td className="px-3 py-2 font-medium">{item.material_name}</td>
                      <td className="px-3 py-2 text-stone-500">{item.ordered_quantity}</td>
                      <td className="px-3 py-2 text-green-600">{item.already_received}</td>
                      <td className="px-3 py-2 text-orange-600">{item.remaining}</td>
                      <td className="px-3 py-2 w-28">
                        {item.remaining > 0 ? (
                          <Input
                            type="number"
                            value={item.receive_quantity}
                            onChange={(e) => {
                              const val = Math.min(parseFloat(e.target.value) || 0, item.remaining);
                              setReceiveItems(prev =>
                                prev.map((it, i) => i === idx ? { ...it, receive_quantity: val } : it)
                              );
                            }}
                            className="w-full h-8 shadow-inner"
                            min={0}
                            max={item.remaining}
                          />
                        ) : (
                          <span className="text-green-600 font-medium text-xs">Fully Received</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile List View */}
              <div className="sm:hidden flex flex-col gap-3">
                {receiveItems.map((item, idx) => (
                  <div key={`mobile-rx-${item.material_id}`} className={`p-3 rounded-lg border ${item.remaining === 0 ? 'bg-green-50/30 border-green-100' : 'bg-white border-stone-200'}`}>
                    <div className="font-medium text-stone-800 mb-2">{item.material_name}</div>
                    <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                      <div>
                        <span className="text-stone-500 block mb-0.5">Ordered</span>
                        <span className="font-mono">{item.ordered_quantity}</span>
                      </div>
                      <div>
                        <span className="text-stone-500 block mb-0.5">Received</span>
                        <span className="font-mono text-green-600">{item.already_received}</span>
                      </div>
                      <div>
                        <span className="text-stone-500 block mb-0.5">Remaining</span>
                        <span className="font-mono text-orange-600">{item.remaining}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-stone-500 block mb-1">Receive Now</span>
                      {item.remaining > 0 ? (
                        <Input
                          type="number"
                          value={item.receive_quantity}
                          onChange={(e) => {
                            const val = Math.min(parseFloat(e.target.value) || 0, item.remaining);
                            setReceiveItems(prev =>
                              prev.map((it, i) => i === idx ? { ...it, receive_quantity: val } : it)
                            );
                          }}
                          className="w-full h-9"
                          min={0}
                          max={item.remaining}
                        />
                      ) : (
                        <span className="text-green-600 font-medium text-sm">Fully Received</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 border-t border-stone-100 pt-4">
              <Button variant="outline" onClick={() => setReceiveDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
              <Button onClick={handlePartialReceive} className="w-full sm:w-auto bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white shadow shadow-[hsl(145,60%,80%)]">
                Confirm Receipt
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;